import React, { useEffect, useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { isProduct, ProductId } from "multitenancy/product";
import { routeElements } from "routers";

import { useAppSelector } from "store/rootReducer";

import { LeftMenu } from "features/menus/Left";

// initialized inside component with translated titles

interface MenuItem {
  id: string;
  menuTitle: string;
  locale: string;
  read: boolean;
  datacy: string;
}

const previewItems = [
  {
    name: "HearingSource",
    titleKey: "administration.hearingSources.title",
  },
  {
    name: "HearingFileType",
    titleKey: "administration.hearingFileTypes.title",
  },
  {
    name: "HearingFileTypeAttendantRole",
    titleKey: "administration.hearingFileTypeAttendantRoles.title",
  },
];

export const Administration: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { profileName } = useAppSelector((state) => state.profile);
  const { permissions } = useAppSelector((state) => state.permissions);

  const listMenusOpen = useRef([
    t("administration.sections.registers").toLowerCase(),
    t("administration.sections.settings").toLowerCase(),
  ]);

  interface RouteElement {
    props?: {
      path?: string;
      children?: RouteElement | RouteElement[];
    };
  }

  const getAllRoutes = (children: RouteElement[]): string[] => {
    const routes: string[] = [];
    children.forEach((route) => {
      if (route?.props?.path) {
        routes.push(route.props.path);
      }
      if (route?.props?.children) {
        const nestedChildren = Array.isArray(route.props.children)
          ? route.props.children
          : [route.props.children];
        nestedChildren.forEach((child) => {
          if (child?.props?.path) {
            const parentPath = route.props?.path || "";
            const childPath = child.props.path;
            const fullPath = childPath.startsWith("/")
              ? childPath
              : `${parentPath}/${childPath}`;
            routes.push(fullPath);
          }
        });
      }
    });
    return routes;
  };

  const allRoutes = getAllRoutes(
    routeElements.props.children as RouteElement[],
  );

  const filteredRoutes = allRoutes
    .filter((r) => r?.startsWith("/administration") || r?.startsWith("/report"))
    .map((r) => r?.split("/").pop()?.toUpperCase() || "");

  const formKeyMap: Record<string, string> = {
    AttendantRole: "administration.attendantRoles.title",
    ServiceIntegrationImportation:
      "administration.integrationImportation.title",
    CustomerTemplate: "administration.templates.title",
    Attendant: "administration.attendants.title",
    IntegrationNotify: "administration.integrationNotify.title",
    HearingType: "administration.hearingTypes.title",
    HearingSource: "administration.hearingSources.title",
    HearingFileType: "administration.hearingFileTypes.title",
    HearingFileTypeAttendantRole:
      "administration.hearingFileTypeAttendantRoles.title",
    UserCustomer: "administration.users.title",
    Place: "administration.units.title",
    HearingTypeAttendantRole: "administration.hearingTypeAttendantRoles.title",
    CustomerCertificate: "administration.certificate.title",
    UserCustomerProfiles: "administration.profiles.title",
    RoleMapAd: "adGroupMapping.title",
    SystemConfiguration: "administration.systemConfiguration.title",
    SystemWatermark: "administration.watermark.title",
  };

  const formattedPermissions = useMemo(() => {
    const filteredPermissions =
      permissions?.filter(
        (permission) => permission.menu?.toUpperCase() === "ADMINISTRAÇÃO",
      ) ?? [];
    const cadastros: MenuItem[] = [];
    const configuracoes: MenuItem[] = [];

    filteredPermissions.forEach((permission) => {
      const hasRead = permission.listObjectUserProfileActionDTOs.some(
        (action: { action: string }) => action.action === "Read",
      );
      if (!hasRead) return;

      const menuItem: MenuItem = {
        id: permission.formId,
        menuTitle: t(formKeyMap[permission.name] || permission.description),
        read: filteredRoutes.includes(permission.name?.toUpperCase()),
        locale: `/administration/${permission.name}`,
        datacy: permission.name?.toLowerCase(),
      };

      if (isProduct(ProductId.Inquerito)) {
        previewItems.forEach(({ name }) => {
          if (permission.name === name) {
            menuItem.read = hasRead;
          }
        });
      }

      if (permission.url?.toLowerCase().includes("cadastros")) {
        cadastros.push(menuItem);
      }
      if (permission.url?.toLowerCase().includes("configurações")) {
        configuracoes.push(menuItem);
      }
    });

    cadastros.sort((a, b) => a.menuTitle.localeCompare(b.menuTitle));
    configuracoes.sort((a, b) => a.menuTitle.localeCompare(b.menuTitle));

    return [
      { title: t("administration.sections.registers"), navigation: cadastros },
      {
        title: t("administration.sections.settings"),
        navigation: configuracoes,
      },
    ].filter((section) => section.navigation.length > 0);
  }, [permissions, t]);

  useEffect(() => {
    if (profileName === "Operador") {
      navigate("/audiences", { replace: true });
      return;
    }

    const isAdminLocation =
      location.pathname === "/administration" ||
      location.pathname === "/administration/";

    if (isAdminLocation) {
      const allAllowed = formattedPermissions.flatMap((sec) => sec.navigation);
      if (allAllowed.length > 0) {
        navigate(allAllowed[0].locale, { replace: true });
      }
      if (allAllowed.length === 0) {
        navigate("/dashboard", { replace: true });
      }

      return;
    }

    if (
      !formattedPermissions
        .flatMap((sec) => sec.navigation)
        .some((item) => {
          const splitPath = item.locale.split("/").pop()?.toUpperCase() || "";
          return splitPath === location.pathname.toUpperCase().split("/").pop();
        })
    ) {
      navigate("/administration", { replace: true });
    }
  }, [profileName, location.pathname, formattedPermissions, navigate]);

  return (
    <LeftMenu
      currentLocale={location.pathname}
      type="administration"
      menuTitle={t("administration.title")}
      data-cy="administracao"
      menuList={formattedPermissions}
      backgroundHeader="primary"
      navigate={navigate}
      listMenusOpen={listMenusOpen.current}
    />
  );
};
